import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface Permission {
  id: string;
  type: 'user' | 'group' | 'tenant';
  targetId: string;
  targetName: string;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  canShare: boolean;
  grantedBy: string;
  grantedAt: string;
  expiresAt?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface Group {
  id: string;
  name: string;
  description?: string;
}

interface PermissionManagementViewProps {
  documentId?: string;
}

const PermissionManagementView: React.FC<PermissionManagementViewProps> = ({ documentId }) => {
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);

  // Form state
  const [selectedEntity, setSelectedEntity] = useState<any>(null);
  const [entityType, setEntityType] = useState<'user' | 'group'>('user');
  const [permissionRights, setPermissionRights] = useState({
    canRead: true,
    canWrite: false,
    canDelete: false,
    canShare: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Simulate API calls
        setTimeout(() => {
          const mockPermissions: Permission[] = [
            {
              id: 'perm1',
              type: 'user',
              targetId: 'user1',
              targetName: 'John Doe (john.doe@company.com)',
              canRead: true,
              canWrite: true,
              canDelete: false,
              canShare: true,
              grantedBy: 'Admin User',
              grantedAt: new Date().toISOString(),
            },
            {
              id: 'perm2',
              type: 'group',
              targetId: 'group1',
              targetName: 'Finance Team',
              canRead: true,
              canWrite: false,
              canDelete: false,
              canShare: false,
              grantedBy: 'Admin User',
              grantedAt: new Date(Date.now() - 86400000).toISOString(),
            },
            {
              id: 'perm3',
              type: 'user',
              targetId: 'user2',
              targetName: 'Jane Smith (jane.smith@company.com)',
              canRead: true,
              canWrite: false,
              canDelete: false,
              canShare: false,
              grantedBy: 'John Doe',
              grantedAt: new Date(Date.now() - 172800000).toISOString(),
            },
          ];

          const mockUsers: User[] = [
            { id: 'user3', name: 'Mike Johnson', email: 'mike.johnson@company.com' },
            { id: 'user4', name: 'Sarah Wilson', email: 'sarah.wilson@company.com' },
            { id: 'user5', name: 'David Brown', email: 'david.brown@company.com' },
          ];

          const mockGroups: Group[] = [
            { id: 'group2', name: 'Marketing Team', description: 'Marketing department members' },
            { id: 'group3', name: 'Engineering Team', description: 'Software engineering team' },
            { id: 'group4', name: 'Executive Team', description: 'Senior management team' },
          ];

          setPermissions(mockPermissions);
          setUsers(mockUsers);
          setGroups(mockGroups);
          setLoading(false);
        }, 800);
      } catch (err) {
        setError(t('permissions.loadFailed'));
        setLoading(false);
      }
    };

    if (documentId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the load-error message; excluded so a language change doesn't refetch permissions (PR #790)
  }, [documentId]);

  const getPermissionIcon = (type: Permission['type']) => {
    switch (type) {
      case 'user':
        return <PersonIcon />;
      case 'group':
        return <GroupIcon />;
      case 'tenant':
        return <BusinessIcon />;
      default:
        return <SecurityIcon />;
    }
  };

  const getPermissionChips = (permission: Permission) => {
    const chips = [];
    if (permission.canRead) chips.push(<Chip key="read" label={t('permissions.read')} size="small" color="primary" variant="outlined" />);
    if (permission.canWrite) chips.push(<Chip key="write" label={t('permissions.write')} size="small" color="secondary" variant="outlined" />);
    if (permission.canDelete) chips.push(<Chip key="delete" label={t('permissions.delete')} size="small" color="error" variant="outlined" />);
    if (permission.canShare) chips.push(<Chip key="share" label={t('permissions.share')} size="small" color="info" variant="outlined" />);
    return chips;
  };

  const handleAddPermission = () => {
    setEditingPermission(null);
    setSelectedEntity(null);
    setEntityType('user');
    setPermissionRights({
      canRead: true,
      canWrite: false,
      canDelete: false,
      canShare: false,
    });
    setAddDialogOpen(true);
  };

  const handleEditPermission = (permission: Permission) => {
    setEditingPermission(permission);
    setEntityType(permission.type as 'user' | 'group');
    setPermissionRights({
      canRead: permission.canRead,
      canWrite: permission.canWrite,
      canDelete: permission.canDelete,
      canShare: permission.canShare,
    });
    setAddDialogOpen(true);
  };

  const handleDeletePermission = (permission: Permission) => {
    // Implement delete logic
    setPermissions(prev => prev.filter(p => p.id !== permission.id));
  };

  const handleSavePermission = () => {
    if (editingPermission) {
      // Update existing permission
      setPermissions(prev => prev.map(p =>
        p.id === editingPermission.id
          ? { ...p, ...permissionRights }
          : p
      ));
    } else if (selectedEntity) {
      // Add new permission
      const newPermission: Permission = {
        id: `perm_${Date.now()}`,
        type: entityType,
        targetId: selectedEntity.id,
        targetName: entityType === 'user'
          ? `${(selectedEntity as User).name} (${(selectedEntity as User).email})`
          : (selectedEntity as Group).name,
        ...permissionRights,
        grantedBy: 'Current User',
        grantedAt: new Date().toISOString(),
      };
      setPermissions(prev => [...prev, newPermission]);
    }
    setAddDialogOpen(false);
  };

  const handleDialogClose = () => {
    setAddDialogOpen(false);
    setEditingPermission(null);
    setSelectedEntity(null);
  };

  const handlePermissionChange = (permission: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setPermissionRights(prev => ({
      ...prev,
      [permission]: event.target.checked,
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  const availableEntities = entityType === 'user' ? users : groups;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="body1" color="text.secondary">
          {t('permissions.manageDescription')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPermission}
        >
          {t('permissions.addPermission')}
        </Button>
      </Box>

      {permissions.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <SecurityIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('permissions.noPermissions')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('permissions.noPermissionsDescription')}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('permissions.entity')}</TableCell>
                <TableCell>{t('permissions.type')}</TableCell>
                <TableCell>{t('documents.permissions')}</TableCell>
                <TableCell>{t('permissions.grantedBy')}</TableCell>
                <TableCell>{t('permissions.grantedDate')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.map((permission) => (
                <TableRow key={permission.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {getPermissionIcon(permission.type)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">
                          {permission.targetName}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`permissions.types.${permission.type}`)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {getPermissionChips(permission)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {permission.grantedBy}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(permission.grantedAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEditPermission(permission)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeletePermission(permission)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit Permission Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingPermission ? t('permissions.editPermission') : t('permissions.addPermission')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {!editingPermission && (
              <>
                <FormControl component="fieldset" sx={{ mb: 3 }}>
                  <FormLabel component="legend">{t('permissions.entityType')}</FormLabel>
                  <Box display="flex" gap={2} mt={1}>
                    <Button
                      variant={entityType === 'user' ? 'contained' : 'outlined'}
                      startIcon={<PersonIcon />}
                      onClick={() => setEntityType('user')}
                    >
                      {t('permissions.types.user')}
                    </Button>
                    <Button
                      variant={entityType === 'group' ? 'contained' : 'outlined'}
                      startIcon={<GroupIcon />}
                      onClick={() => setEntityType('group')}
                    >
                      {t('permissions.types.group')}
                    </Button>
                  </Box>
                </FormControl>

                <Autocomplete
                  value={selectedEntity}
                  onChange={(_, newValue) => setSelectedEntity(newValue)}
                  options={availableEntities}
                  getOptionLabel={(option) =>
                    entityType === 'user'
                      ? `${(option as User).name} (${(option as User).email})`
                      : (option as Group).name
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('permissions.selectEntity', {
                        type: t(`permissions.types.${entityType}`).toLowerCase(),
                      })}
                      fullWidth
                    />
                  )}
                  sx={{ mb: 3 }}
                />

                <Divider sx={{ mb: 3 }} />
              </>
            )}

            <FormControl component="fieldset">
              <FormLabel component="legend">{t('documents.permissions')}</FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={permissionRights.canRead}
                      onChange={handlePermissionChange('canRead')}
                      color="primary"
                    />
                  }
                  label={t('permissions.readDescription')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={permissionRights.canWrite}
                      onChange={handlePermissionChange('canWrite')}
                      color="secondary"
                    />
                  }
                  label={t('permissions.writeDescription')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={permissionRights.canShare}
                      onChange={handlePermissionChange('canShare')}
                      color="info"
                    />
                  }
                  label={t('permissions.shareDescription')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={permissionRights.canDelete}
                      onChange={handlePermissionChange('canDelete')}
                      color="error"
                    />
                  }
                  label={t('permissions.deleteDescription')}
                />
              </FormGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSavePermission}
            variant="contained"
            disabled={!editingPermission && !selectedEntity}
          >
            {editingPermission ? t('common.update') : t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionManagementView;
